import * as Yup from 'yup';
import { Field, Formik } from 'formik';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import { useTranslation, Trans } from 'react-i18next';

import { ISkillFragment } from 'graphql/types';
import { Button } from 'common/components/Button';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { ImageField, TextField } from 'common/components/FormField';
import { FormSection } from 'common/components/FormSection';
import { Link } from 'common/components/Link';
import { TSkillFormValues } from 'skill/types';
import { CARD_IMAGE_MAX_SIZE, CARD_IMAGE_TYPE } from 'common/constants/files';
import { IMAGE_VARIANTS } from 'common/constants/image';
import { EImageVariant } from 'common/types';
import { Banner } from 'common/components/Banner';
import { Img } from 'common/components/Img';
import SkillCardImage from 'assets/images/module-card-image.webp';

import { SkillCertificateField } from './SkillCertificateField';
import { SkillGroupField } from './SkillGroupField';

const SHORT_NAME_MAX_LENGTH = 12;

interface IProps {
    disabled?: boolean;
    skill?: ISkillFragment;
    isEdit?: boolean;
    isEditable?: boolean;
    initialValues: TSkillFormValues;
    onClose?(): void;
    onDelete?(): void;
    onSubmit(values: TSkillFormValues): void;
}

export const SkillForm = ({
    disabled,
    skill,
    isEdit,
    isEditable = true,
    onClose,
    onDelete,
    onSubmit,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const smallScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.only('xs')
    );

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(translate('validate.required')),
        shortName: Yup.string().max(
            SHORT_NAME_MAX_LENGTH,
            translate('validate.maxCharacters', {
                count: SHORT_NAME_MAX_LENGTH,
            })
        ),
    });

    const submitText = translate(
        `skillForm.${isEdit ? 'editButton' : 'createButton'}`
    );

    return (
        <Formik
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            {...other}
        >
            {({ submitForm, isSubmitting, dirty, values }) => (
                <Box sx={{ pt: { xs: 2, sm: 4 } }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting}
                        onConfirm={submitForm}
                    />

                    <Typography variant="h3">
                        {isEdit
                            ? translate('skillForm.editSkill')
                            : translate('skillForm.createSkill')}
                    </Typography>

                    {!isEdit && (
                        <Box sx={{ mt: 2 }}>
                            <Typography>
                                {translate('skillForm.createSkillIntro')}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ sm: 9, xs: 8 }}>
                                <Field
                                    component={TextField}
                                    label={translate('name')}
                                    name="name"
                                />
                            </Grid>

                            <Grid size={{ sm: 3, xs: 4 }}>
                                <Field
                                    component={TextField}
                                    label={translate('shortName')}
                                    name="shortName"
                                />
                            </Grid>
                        </Grid>

                        <Field
                            multiline
                            component={TextField}
                            label={translate('description')}
                            name="description"
                        />

                        <FormSection
                            description={
                                <Trans
                                    components={{
                                        a: (
                                            <Link
                                                rel="noreferrer"
                                                underline="always"
                                            />
                                        ),
                                    }}
                                    i18nKey="skillForm.imageDescription"
                                />
                            }
                            title={translate('image')}
                        >
                            <Stack
                                direction={
                                    smallScreen ? 'column-reverse' : 'row'
                                }
                                spacing={2}
                                sx={{ mt: 2 }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <Field
                                        acceptedFileTypes={CARD_IMAGE_TYPE}
                                        component={ImageField}
                                        helperText={translate(
                                            'imageSizeDescription',
                                            {
                                                width: IMAGE_VARIANTS[
                                                    EImageVariant.Card
                                                ].minWidth,
                                                height: IMAGE_VARIANTS[
                                                    EImageVariant.Card
                                                ].minHeight,
                                            }
                                        )}
                                        imageVariant={EImageVariant.Card}
                                        label={translate('image')}
                                        maxUploadSize={CARD_IMAGE_MAX_SIZE}
                                        name="thumbnail"
                                    />
                                </Box>
                                <Box sx={{ width: '100%' }}>
                                    <Banner>
                                        <Box sx={{ py: 1, pr: 1, pl: 2 }}>
                                            {translate(
                                                'skillForm.imageBannerDescription'
                                            )}
                                        </Box>
                                        <Box
                                            sx={{
                                                mt: 'auto',
                                            }}
                                        >
                                            <Img src={SkillCardImage} />
                                        </Box>
                                    </Banner>
                                </Box>
                            </Stack>
                        </FormSection>

                        <FormSection
                            renderExpanded
                            description={
                                <Box>
                                    <Trans
                                        components={{
                                            a: (
                                                <Link
                                                    rel="noreferrer"
                                                    underline="always"
                                                />
                                            ),
                                        }}
                                        i18nKey="skillForm.groupsDescription"
                                    />
                                </Box>
                            }
                            title={translate('skillForm.groups')}
                        >
                            <Box sx={{ mt: 2 }}>
                                <SkillGroupField
                                    name="groups"
                                    value={values.groups || []}
                                />
                            </Box>
                        </FormSection>

                        <FormSection
                            renderExpanded
                            description={
                                <Box>
                                    <Trans
                                        components={{
                                            a: (
                                                <Link
                                                    rel="noreferrer"
                                                    underline="always"
                                                />
                                            ),
                                        }}
                                        i18nKey="skillForm.certificatesDescription"
                                    />
                                </Box>
                            }
                            title={translate('skillForm.certificates')}
                        >
                            <Box sx={{ mt: 2 }}>
                                <SkillCertificateField
                                    name="certificates"
                                    value={values.certificates || []}
                                />
                            </Box>
                        </FormSection>
                    </Box>

                    <Box sx={{ display: 'flex', mt: 4 }}>
                        <Button
                            color="primary"
                            disabled={
                                isSubmitting ||
                                disabled ||
                                (!isSubmitting && !disabled && !dirty)
                            }
                            loading={isSubmitting}
                            type="submit"
                            variant="contained"
                            onClick={(
                                e: React.MouseEvent<HTMLButtonElement>
                            ) => {
                                e.preventDefault();

                                return submitForm();
                            }}
                        >
                            {submitText}
                        </Button>

                        {onClose && (
                            <Box sx={{ ml: 2 }}>
                                <Button
                                    disabled={isSubmitting}
                                    variant="contained"
                                    onClick={onClose}
                                >
                                    {translate('cancel')}
                                </Button>
                            </Box>
                        )}

                        {isEditable && isEdit && (
                            <Box sx={{ ml: 'auto' }}>
                                <FormDeleteButton
                                    disabled={isSubmitting}
                                    onClick={onDelete}
                                >
                                    {translate('delete')}
                                </FormDeleteButton>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Formik>
    );
};
